import { Injectable, NgModule } from "@angular/core";
import { AccountAdminGuard, LoggedInGuard } from "../auth/auth.guards";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { AppsListComponent } from "./apps-list/apps-list.component";
import { AppService } from "./app.service";
import { SessionService } from "../session.service";
import { map, take } from "rxjs/operators";
import { AccountAppComponent } from "./account-app/account-app.component";
import { PermissionService } from "../permission.service";
import { from, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
class WriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.accountApps.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
export class AppsResolve implements Resolve<any> {
  constructor(
    private AppsService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AppsService.getApps(
      this.SessionService.currentAccountValue!.accountID,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class AppResolve implements Resolve<any> {
  constructor(
    private AppsService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppsService.getApp(
      this.SessionService.currentAccountValue!.accountID,
      route.params.appID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class SubscriptionResolve implements Resolve<any> {
  constructor(
    private AppsService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppsService.getSubscription(
      this.SessionService.currentAccountValue!.accountID,
      route.params.appID,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "admin" },
    children: [
      {
        path: "list",
        component: AppsListComponent,
        data: { title: "Apps List" },
        resolve: {
          apps: AppsResolve,
        },
        canActivate: [AccountAdminGuard],
      },
      {
        path: "create",
        component: AccountAppComponent,
        data: { title: "Create New App" },
        canActivate: [WriteGuard],
      },
      {
        path: ":appID",
        component: AccountAppComponent,
        data: { title: "App" },
        resolve: {
          app: AppResolve,
          subscription: SubscriptionResolve,
        },
        canActivate: [WriteGuard],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AppsRoutingModule {}

const appsModuleLazyLoadRoutes: Routes = [
  {
    path: "apps",
    loadChildren: () => import("./apps.module").then((m) => m.AppsModule),
  },
];

export { appsModuleLazyLoadRoutes };
